import { useCallback, useContext } from 'react';
import { GlobalContext } from '../global-context';
import { authService } from '../services/auth.service';

/**
 * The permissions the app checks: legacy's six (keep in sync with
 * @edulead/models/src/auth/Permission.ts) and the Go API's beacons:manage (api/v4/beacons.md).
 */
const PERMISSIONS = [
  'crm-connection:manage',
  'crm-field-mappings:edit',
  'export-institutions-crm:execute',
  'export-institutions-file:execute',
  'export-people-crm:execute',
  'export-people-file:execute',
  'beacons:manage',
] as const;
type Permission = (typeof PERMISSIONS)[number];

interface UsePermissionResult {
  hasPermission: (permission: Permission) => boolean;
  permissions: string[];
  isLoading: boolean;
  role: string;
  refetch: () => Promise<void>;
}

/**
 * Hook for checking user permissions.
 * Permissions are loaded once at app startup and stored in GlobalContext.
 *
 * Usage:
 * ```tsx
 * const { hasPermission, isLoading } = usePermission();
 *
 * if (isLoading) return <Loading />;
 *
 * return (
 *   <>
 *     {hasPermission('field-mappings:edit') && <EditButton />}
 *   </>
 * );
 * ```
 */
export function usePermission(): UsePermissionResult {
  const globalContext = useContext(GlobalContext);

  // Read directly from context - no API call needed
  const permissions = globalContext?.authInfo?.permissions || [];
  const role = globalContext?.authInfo?.role || 'MEMBER';

  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      return permissions.includes(permission);
    },
    [permissions]
  );

  // refetch triggers full authInfo refresh via onSetState
  const refetch = useCallback(async () => {
    if (globalContext?.onSetState) {
      const authInfo = await authService.fetchAuthInfo();
      globalContext.onSetState(authInfo);
    }
  }, [globalContext]);

  return {
    hasPermission,
    permissions,
    isLoading: !globalContext?.authInfo,
    role,
    refetch
  };
}
